import { SupabaseClient } from "@supabase/supabase-js";
import { supabase } from "../../config/supabaseClient";
import { getClientIp } from "./getClientIp";
import { templateRouteName } from "./templateAnalyticsEvents";

const IP_CACHE_KEY = "sb_template_analytics_ip";

/** In-memory IP for this page lifetime (sync read on every track). */
let cachedIp: string | null = null;
/** In-flight lookup so concurrent tracks share one IP request. */
let ipLookupPromise: Promise<string> | null = null;

function readCachedIp(): string {
  if (cachedIp) return cachedIp;
  if (typeof sessionStorage === "undefined") return "";
  try {
    const fromSession = sessionStorage.getItem(IP_CACHE_KEY) || "";
    if (fromSession) cachedIp = fromSession;
    return fromSession;
  } catch {
    return "";
  }
}

function writeCachedIp(ip: string) {
  if (!ip) return;
  cachedIp = ip;
  if (typeof sessionStorage === "undefined") return;
  try {
    sessionStorage.setItem(IP_CACHE_KEY, ip);
  } catch {
    // private mode / quota — memory cache still works
  }
}

/**
 * Never blocks tracking inserts. Returns a sync cache hit immediately, and
 * warms the cache in the background for later events in this session.
 */
function resolveClientIpNonBlocking(): string {
  const hit = readCachedIp();
  if (hit) return hit;

  if (!ipLookupPromise) {
    ipLookupPromise = getClientIp()
      .then((ip) => {
        writeCachedIp(ip);
        return ip;
      })
      .catch(() => "")
      .finally(() => {
        ipLookupPromise = null;
      });
  }
  return "";
}

export enum TEMPLATE_TRACKING_TYPES {
  PAGE = "page",
  EVENT = "event",
  RENDER = "render",
  ERROR = "error",
  SEARCH = "search",
}

export type TemplateTrackExtras = {
  pathname?: string;
  details?: Record<string, unknown> | string;
  title?: string;
  user_id?: string;
  source?: string;
  username?: string;
  route_name?: string;
  page_key?: string;
  section_id?: string;
  section_type?: string;
  entity_type?: string;
  entity_id?: string;
  entity_label?: string;
  carousel_slug?: string;
  shop_browse?: Record<string, unknown>;
};

function normalizeDetails(
  details: TemplateTrackExtras["details"],
): Record<string, unknown> | string | null {
  if (details == null || details === "") return null;
  if (typeof details === "string") return details;
  return details;
}

/**
 * Append-only writer for public Master Template (`/website/:username`) analytics.
 * Fire-and-forget at call sites — do not await unless testing.
 */
export default async function trackTemplate(
  name: string,
  type: TEMPLATE_TRACKING_TYPES,
  extras?: TemplateTrackExtras,
  client: SupabaseClient = supabase,
) {
  if (
    typeof name !== "string" ||
    !Object.values(TEMPLATE_TRACKING_TYPES).includes(type)
  ) {
    return;
  }

  const userId = extras?.user_id;
  if (!userId) return;

  const routeName =
    extras?.route_name ||
    (extras?.username ? templateRouteName(extras.username) : "");

  let success = false;
  const trackingObject = {
    // Sync cache only — never await IP before insert (search analytics latency).
    ip: resolveClientIpNonBlocking(),
    type,
    name,
    pathname: extras?.pathname || "",
    details: normalizeDetails(extras?.details),
    title: extras?.title || "Website Template",
    user_id: userId,
    source: extras?.source || "",
    route_name: routeName,
    page_key: extras?.page_key || null,
    section_id: extras?.section_id || null,
    section_type: extras?.section_type || null,
    entity_type: extras?.entity_type || null,
    entity_id: extras?.entity_id || null,
    entity_label: extras?.entity_label || null,
    carousel_slug: extras?.carousel_slug || null,
    shop_browse: extras?.shop_browse || null,
  };

  try {
    const { error } = await client
      .from("template_analytics")
      .insert([trackingObject]);
    if (!error) success = true;
  } catch {
    try {
      const { error } = await client.from("template_analytics").insert([
        {
          ...trackingObject,
          ip: "",
          type: TEMPLATE_TRACKING_TYPES.ERROR,
          name: "Tracking Error",
        },
      ]);
      if (!error) success = true;
    } catch {
      // swallow — analytics must never break the public site
    }
  } finally {
    if (!success) {
      // intentionally silent
    }
  }
}
